import { MessageSquare, Paperclip, Calendar, ListChecks } from 'lucide-react';
import type { Task } from '../../types';
import { useApp } from '../../context/AppContext';
import { getPriorityConfig, formatDate } from '../../utils/helpers';
import { AvatarGroup } from '../common/Avatar';
import Badge from '../common/Badge';
import { LabelBadge } from '../common/Badge';

interface KanbanCardProps {
  task: Task;
}

export default function KanbanCard({ task }: KanbanCardProps) {
  const { dispatch, getUserById, getLabelById } = useApp();
  const priority = getPriorityConfig(task.priority);
  const assignees = task.assigneeIds.map((id) => getUserById(id)).filter(Boolean);
  const taskLabels = task.labelIds.map((id) => getLabelById(id)).filter(Boolean);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('taskId', task.id);
    e.dataTransfer.effectAllowed = 'move';
    (e.target as HTMLElement).classList.add('dragging');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).classList.remove('dragging');
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => dispatch({ type: 'SELECT_TASK', payload: task.id })}
      className="bg-white rounded-xl border border-slate-200 p-3.5 cursor-pointer hover:shadow-md hover:border-brand-200 transition-all duration-200 group active:scale-[0.98]"
      role="button"
      tabIndex={0}
      aria-label={`Task: ${task.title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter') dispatch({ type: 'SELECT_TASK', payload: task.id });
      }}
    >
      {taskLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {taskLabels.map((label) => (
            <LabelBadge key={label!.id} name={label!.name} color={label!.color} />
          ))}
        </div>
      )}

      <h4 className="text-sm font-medium text-slate-800 mb-2 leading-snug group-hover:text-brand-600 transition-colors">
        {task.title}
      </h4>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Badge className={priority.color} dot={priority.dot}>
          {priority.label}
        </Badge>
        {task.storyPoints > 0 && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-100 text-xs font-semibold text-slate-600">
            {task.storyPoints}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-slate-400">
          {task.comments.length > 0 && (
            <span className="flex items-center gap-1 text-xs">
              <MessageSquare size={13} />
              {task.comments.length}
            </span>
          )}
          {task.attachments.length > 0 && (
            <span className="flex items-center gap-1 text-xs">
              <Paperclip size={13} />
              {task.attachments.length}
            </span>
          )}
          {task.subtasks.length > 0 && (
            <span className="flex items-center gap-1 text-xs">
              <ListChecks size={13} />
              <span>
                {task.subtasks.filter((s) => s.completed).length}/{task.subtasks.length}
              </span>
            </span>
          )}
          {task.dueDate && (
            <span className="flex items-center gap-1 text-xs">
              <Calendar size={13} />
              {formatDate(task.dueDate)}
            </span>
          )}
        </div>

        {assignees.length > 0 && (
          <AvatarGroup
            users={assignees.map((u) => ({ name: u!.name, color: u!.avatarColor }))}
            max={2}
            size="sm"
          />
        )}
      </div>
    </div>
  );
}
